// Main-thread handle for one source: owns the worker, the full-resolution frame cache (L2) and proxies (L1).

import type { DecodedFrame, DeliveryMode, FromWorker, OpenedInfo, ToWorker } from './protocol';
import type { VideoSpec } from '../core/types';
import type { ExrView } from '../core/sequence';
import { formatBitDepth, formatChroma } from './codec-info';

export interface SourceClientEvents {
  onFrame?: (index: number) => void;
  onProxy?: (index: number) => void;
  onScanProgress?: (done: number, total: number) => void;
  onError?: (detail: string) => void;
  /** The first decoded frame showed a different picture size than the container said. */
  onSpecChanged?: (prev: VideoSpec) => void;
}

/** An image sequence handed to the sequence worker (F-01.12). */
export interface SequenceInput {
  kind: 'png' | 'exr';
  /** Name pattern, e.g. "shot_####.exr". */
  pattern: string;
  /** Frame number of files[0]. */
  first: number;
  /** Files in frame order; null where a frame number has no file. */
  files: (File | null)[];
  fps: number;
  exrView?: ExrView;
}

export type MediaInput = File | SequenceInput;

export function isSequence(m: MediaInput): m is SequenceInput {
  return !(m instanceof Blob);
}

export function mediaName(m: MediaInput): string {
  return isSequence(m) ? m.pattern : m.name;
}

const s0 = (i: OpenedInfo) => i.spec;

export class SourceClient {
  /** Enables verbose worker logging (development only). */
  static debug = false;
  private static nextUid = 1;
  /** Unique per opened video: cache keys include it so a slot's new video never shows the previous one's frames. */
  readonly uid = SourceClient.nextUid++;
  /** A video file, or an image sequence (F-01.12). */
  readonly media: MediaInput;
  info: OpenedInfo | null = null;
  private worker: Worker;
  private cache = new Map<number, DecodedFrame>();
  private cacheBytes = 0;
  private proxies = new Map<number, Blob>();
  private proxyBitmaps = new Map<number, ImageBitmap>();
  private proxyDecoding = new Set<number>();
  private generation = 0;
  private lastRequestKey = '';
  private pinned = new Set<number>();
  readonly frameErrors = new Map<number, string>();
  private frameErrorAt = new Map<number, number>();
  /** Every frame error seen (diagnostics, E-21). */
  readonly errorLog: { index: number; detail: string; at: number }[] = [];
  private lastSendAt = 0;
  budgetBytes = 1024 * 1024 * 1024;
  scanDone = false;
  /** Increments whenever a proxy or proxy bitmap becomes available (timeline thumbnails redraw). */
  proxyVersion = 0;
  scanProgress = 0;
  events: SourceClientEvents = {};
  private frameListeners = new Set<(index: number) => void>();

  /** Additional frame listeners (waiters); returns an unsubscribe function. */
  addFrameListener(f: (index: number) => void): () => void {
    this.frameListeners.add(f);
    return () => this.frameListeners.delete(f);
  }

  private emitFrame(index: number): void {
    this.events.onFrame?.(index);
    for (const f of [...this.frameListeners]) f(index);
  }
  /** Decode timing samples for the performance overlay (E-22). */
  readonly decodeTimes: number[] = [];
  private openResolve: ((i: OpenedInfo) => void) | null = null;
  private openReject: ((e: { errorKey: string; detail: string; spec?: Partial<VideoSpec> }) => void) | null = null;

  constructor(media: MediaInput) {
    this.media = media;
    this.worker = isSequence(media)
      ? new Worker(new URL('./sequence-worker.ts', import.meta.url), { type: 'module' })
      : new Worker(new URL('./source-worker.ts', import.meta.url), { type: 'module' });
    this.worker.onmessage = (ev: MessageEvent<FromWorker>) => this.onMessage(ev.data);
    this.worker.onerror = (ev) => {
      this.events.onError?.(ev.message);
      this.openReject?.({ errorKey: 'error.open.unreadable', detail: ev.message });
    };
  }

  private send(msg: ToWorker): void {
    this.worker.postMessage(msg);
  }

  open(preferSoftware = false): Promise<OpenedInfo> {
    return new Promise((resolve, reject) => {
      this.openResolve = resolve;
      this.openReject = reject;
      const m = this.media;
      if (isSequence(m)) this.send({ type: 'openSeq', files: m.files, kind: m.kind, fps: m.fps, exrView: m.exrView });
      else this.send({ type: 'open', file: m, preferSoftware, debug: SourceClient.debug });
    });
  }

  /** File name, or the sequence pattern. */
  get name(): string {
    return mediaName(this.media);
  }

  /** New EXR view transform (F-01.12): frames are decoded again with it. */
  setExrView(view: ExrView): void {
    if (!isSequence(this.media)) return;
    this.media.exrView = view;
    this.send({ type: 'exrView', view });
    this.clearCache();
    this.request(this.lastFrames, this.lastPin, this.lastMode);
  }

  get spec(): VideoSpec {
    if (!this.info) throw new Error('Source not opened');
    return this.info.spec;
  }

  get frameCount(): number {
    return this.info?.spec.frameCount ?? 0;
  }

  private onMessage(msg: FromWorker): void {
    switch (msg.type) {
      case 'opened':
        this.info = msg.info;
        this.openResolve?.(msg.info);
        this.openResolve = this.openReject = null;
        break;
      case 'openError':
        this.openReject?.({ errorKey: msg.errorKey, detail: msg.detail, spec: msg.spec });
        this.openResolve = this.openReject = null;
        break;
      case 'frame': {
        const f = msg.frame;
        this.refineSpec(f);
        const old = this.cache.get(f.index);
        if (old) this.cacheBytes -= old.data.byteLength;
        this.cache.set(f.index, f);
        this.cacheBytes += f.data.byteLength;
        this.frameErrors.delete(f.index);
        this.decodeTimes.push(f.decodeMs);
        if (this.decodeTimes.length > 120) this.decodeTimes.shift();
        this.evict();
        this.emitFrame(f.index);
        break;
      }
      case 'videoFrame':
        this.storeGpuFrame(msg.index, msg.frame);
        this.frameErrors.delete(msg.index);
        this.emitFrame(msg.index);
        break;
      case 'frameError':
        this.frameErrors.set(msg.index, msg.detail);
        this.frameErrorAt.set(msg.index, performance.now());
        this.errorLog.push({ index: msg.index, detail: msg.detail, at: performance.now() });
        if (this.errorLog.length > 50) this.errorLog.shift();
        this.emitFrame(msg.index);
        break;
      case 'decodeError':
        this.events.onError?.(msg.detail);
        break;
      case 'proxy':
        this.proxies.set(msg.index, msg.blob);
        this.proxyVersion++;
        this.events.onProxy?.(msg.index);
        break;
      case 'scanProgress':
        this.scanProgress = msg.total ? msg.done / msg.total : 1;
        this.events.onScanProgress?.(msg.done, msg.total);
        break;
      case 'scanDone':
        this.scanDone = true;
        this.scanProgress = 1;
        this.events.onScanProgress?.(1, 1);
        break;
    }
  }

  /** The first decoded frame tells the real pixel format (S-02): update bit depth / chroma when they differ. */
  private refineSpec(f: DecodedFrame): void {
    if (!this.info) return;
    if (f.width !== s0(this.info).width || f.height !== s0(this.info).height) {
      const prev = { ...this.info.spec };
      this.info.spec.width = f.width;
      this.info.spec.height = f.height;
      this.events.onSpecChanged?.(prev);
    }
    const bd = formatBitDepth(f.sourceFormat);
    const ch = formatChroma(f.sourceFormat);
    const s = this.info.spec;
    const rgb = f.format === 'RGBA' || f.format === 'RGBA16';
    if (bd && !rgb && bd !== s.bitDepth) s.bitDepth = bd;
    if (ch && !rgb && ch !== s.chromaSubsampling) s.chromaSubsampling = ch;
    if (!s.matrixCoefficients && f.colorSpace.matrix) s.matrixCoefficients = f.colorSpace.matrix;
    if (!s.range && f.colorSpace.fullRange !== null) s.range = f.colorSpace.fullRange ? 'full' : 'limited';
  }

  private evict(): void {
    if (this.cacheBytes <= this.budgetBytes) return;
    for (const [idx, f] of this.cache) {
      if (this.cacheBytes <= this.budgetBytes * 0.9) break;
      if (this.pinned.has(idx)) continue;
      this.cache.delete(idx);
      this.cacheBytes -= f.data.byteLength;
    }
  }

  getFrame(index: number): DecodedFrame | undefined {
    const f = this.cache.get(index);
    if (f) {
      // Refresh LRU position.
      this.cache.delete(index);
      this.cache.set(index, f);
    }
    return f;
  }

  hasFrame(index: number): boolean {
    return this.cache.has(index);
  }

  // GPU frames (S-03 playback path): VideoFrames held for texture upload; must be closed promptly because the
  // hardware decoder has a limited output pool (E-15).
  private gpuFrames = new Map<number, VideoFrame>();
  gpuLimit = 20;

  hasGpuFrame(index: number): boolean {
    return this.gpuFrames.has(index);
  }

  getGpuFrame(index: number): VideoFrame | undefined {
    return this.gpuFrames.get(index);
  }

  /** Removes a GPU frame from the cache without closing it (ownership moves to the caller). */
  takeGpuFrame(index: number): VideoFrame | undefined {
    const f = this.gpuFrames.get(index);
    this.gpuFrames.delete(index);
    return f;
  }

  /** Planes or a GPU frame is available for display. */
  hasAny(index: number): boolean {
    return this.cache.has(index) || this.gpuFrames.has(index);
  }

  /** Closes GPU frames outside [lo, hi] (the playback window). */
  retainGpu(lo: number, hi: number): void {
    for (const [i, f] of this.gpuFrames) {
      if (i < lo || i > hi) {
        f.close();
        this.gpuFrames.delete(i);
      }
    }
  }

  private storeGpuFrame(index: number, frame: VideoFrame): void {
    this.gpuFrames.get(index)?.close();
    this.gpuFrames.set(index, frame);
    while (this.gpuFrames.size > this.gpuLimit) {
      let victim = -1;
      for (const i of this.gpuFrames.keys()) {
        if (!this.pinned.has(i)) {
          victim = i;
          break;
        }
      }
      if (victim < 0) victim = this.gpuFrames.keys().next().value as number;
      this.gpuFrames.get(victim)?.close();
      this.gpuFrames.delete(victim);
    }
  }

  // Frames other modules are waiting for (alignment estimates, export): merged into every request until decoded.
  private extra = new Set<number>();

  /** Ensures a frame gets decoded even while the player keeps replacing the request list. */
  need(index: number): void {
    if (this.cache.has(index)) return;
    this.extra.add(index);
    this.request(this.lastFrames, this.lastPin, this.lastMode);
  }

  private lastFrames: number[] = [];
  private lastPin: number[] = [];
  private lastMode: DeliveryMode = 'planes';

  /** Requests frames in priority order; cached frames are skipped. Pinned frames are protected from eviction. */
  request(frames: number[], pin: number[] = frames.slice(0, 1), mode: DeliveryMode = 'planes'): void {
    this.lastFrames = frames;
    this.lastPin = pin;
    this.lastMode = mode;
    this.pinned = new Set([...pin, ...this.extra]);
    for (const i of this.extra) if (this.cache.has(i)) this.extra.delete(i);
    // Extra frames need exact planes; they go right after the first (on-screen) frame, and the whole request
    // switches to planes until they arrive (rare: alignment estimates while playing).
    if (this.extra.size) {
      frames = [...frames.slice(0, 1), ...this.extra, ...frames.slice(1)];
      mode = 'planes';
    }
    // Failed frames are retried after a short pause so a transient decoder error never blocks a frame forever.
    const now = performance.now();
    for (const [i, at] of this.frameErrorAt) {
      if (now - at > 1500) {
        this.frameErrorAt.delete(i);
        this.frameErrors.delete(i);
      }
    }
    const have = (f: number) => this.cache.has(f) || (mode === 'frame' && !this.extra.has(f) && this.gpuFrames.has(f));
    const missing = frames.filter((f) => f >= 0 && f < this.frameCount && !have(f) && !this.frameErrors.has(f));
    const key = mode + ':' + missing.join(',');
    if (key === this.lastRequestKey && now - this.lastSendAt < 1000) return;
    this.lastRequestKey = key;
    this.lastSendAt = now;
    this.generation++;
    this.send({ type: 'decode', frames: missing, generation: this.generation, mode });
  }

  /** Empties the full-resolution cache (used by the self-test for cold seeks and when memory is low). */
  clearCache(): void {
    this.cache.clear();
    this.cacheBytes = 0;
    this.lastRequestKey = '';
    this.frameErrors.clear();
    this.frameErrorAt.clear();
  }

  /** Drops per-frame errors so they can be retried. */
  clearErrors(): void {
    this.frameErrors.clear();
    this.lastRequestKey = '';
  }

  startScan(width = 480): void {
    this.send({ type: 'scan', width, start: true });
  }

  pauseScan(paused: boolean): void {
    this.send({ type: 'scanPause', paused });
  }

  hasProxy(index: number): boolean {
    return this.proxies.has(index);
  }

  proxyBlob(index: number): Blob | undefined {
    return this.proxies.get(index);
  }

  /** Whether a proxy is ready to draw (decoded to a bitmap, not only its compressed blob). */
  hasProxyBitmap(index: number): boolean {
    return this.proxyBitmaps.has(index);
  }

  /** Starts decoding the proxies of these frames so scrubbing and smooth playback find them ready. */
  warmProxies(indices: Iterable<number>): void {
    for (const i of indices) if (!this.proxyBitmaps.has(i)) this.getProxyBitmap(i);
  }

  /** Returns a decoded proxy bitmap if ready; otherwise starts decoding it and returns undefined. */
  getProxyBitmap(index: number): ImageBitmap | undefined {
    const b = this.proxyBitmaps.get(index);
    if (b) {
      // Most recently used last, so the oldest is released first.
      this.proxyBitmaps.delete(index);
      this.proxyBitmaps.set(index, b);
      return b;
    }
    const blob = this.proxies.get(index);
    if (blob && !this.proxyDecoding.has(index)) {
      this.proxyDecoding.add(index);
      createImageBitmap(blob)
        .then((bmp) => {
          this.proxyBitmaps.set(index, bmp);
          this.proxyVersion++;
          // Keep a window of decoded proxies (480 px wide: about 0.5 MB each).
          if (this.proxyBitmaps.size > 240) {
            const first = this.proxyBitmaps.keys().next().value as number;
            this.proxyBitmaps.get(first)?.close();
            this.proxyBitmaps.delete(first);
          }
          this.events.onProxy?.(index);
        })
        .catch(() => undefined)
        .finally(() => this.proxyDecoding.delete(index));
    }
    return undefined;
  }

  get cachedBytes(): number {
    return this.cacheBytes;
  }

  get cachedCount(): number {
    return this.cache.size;
  }

  dispose(): void {
    this.send({ type: 'close' });
    setTimeout(() => this.worker.terminate(), 200);
    this.cache.clear();
    for (const f of this.gpuFrames.values()) f.close();
    this.gpuFrames.clear();
    for (const b of this.proxyBitmaps.values()) b.close();
    this.proxyBitmaps.clear();
    this.proxies.clear();
  }
}
